'use strict';

// external imports
import React, {
    MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import {DataStructure} from '~/shared/src/utilities/dataStructure';
import {
    ColumnConfig,
    ColumnDefinition,
    ColumnFilter,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';


// frontend imports
import {AllProps as DataTableAllProps} from '~/frontend/src/components/base/DataTable';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import {XlsxExport} from '~/frontend/src/components/base/DataTable/XlsxExport';
import {
    convertSortersToSortersWithField,
    convertSortersWithFieldToSorters,
    getAllColumns,
    applyAllColumnsToColumns
} from '~/frontend/src/components/base/DataTable/utilities';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {setElusiveData} from '~/frontend/src/actions/elusiveData';
import clearSearch from '~/frontend/src/actions/panel/finderOverview/clearSearch';
import {setMessage} from '~/frontend/src/actions/intercom';
import {
    ColumnConfigMenuColumn,
    ColumnConfigMenuColumnsGroup
} from '~/frontend/src/components/base/shared/menu/ColumnConfigMenu';
import {
    ColumnSortMenuColumnSorter,
    ColumnSortMenuSingleColumn
} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';
import {ComponentProps as OverviewComponentProps} from '~/frontend/src/modules/panels/Finder/overview';


export type DefaultProps = {

    /**
     * The structure of the column data.
     */
    dataStructure : DataStructure;

    /**
     * The column definition of the columns possible to show in the DataTable.
     */
    columnDefinitions : ColumnDefinition[];

    /**
     * The column configuration of the columns to show in the DataTable.
     */
    columns : ColumnConfig[];

    filters : ColumnFilter[];

    /**
     * The column sorters.
     */
    sorters : ColumnSorter[];

    /**
     * The current panelProps.
     *
     * Only applicable if <prop>showViewSelector</prop> is set to true.
     */
    viewSelectorPanelProps : PanelProps;

    showDownloadXlsxButton : boolean;

    /**
     * Show clear search button.
     */
    showClearSearchButton : boolean;

    /**
     * Should the ViewSelectorDropdown shown in the toolbar.
     */
    showViewSelector : boolean;

    /**
     * Should the column options button shown in the toolbar.
     */
    showColumnOptionsButton : boolean;

    /**
     * Should the column sort button shown in the toolbar.
     */
    showColumnSortButton : boolean;

    /**
     * Show reload button.
     */
    showReloadButton : boolean;

    tabIndexOffset : number;

    /**
     * The CSS class name(s) to add to the component.
     */
    className : string;
};
export const defaultProps : Required<DefaultProps> = {
    dataStructure          : {} as DataStructure,
    columnDefinitions      : [] as ColumnDefinition[],
    columns                : [] as ColumnConfig[],
    filters                : [] as ColumnFilter[],
    sorters                : [] as ColumnSorter[],
    viewSelectorPanelProps : {} as PanelProps,
    showDownloadXlsxButton : true,
    showClearSearchButton  : true,
    showViewSelector       : true,
    showColumnOptionsButton: true,
    showColumnSortButton   : true,
    showReloadButton       : true,
    tabIndexOffset         : 500,
    className              : ''
};

export type Props = {
    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The database schema to filter data and metadata for.
     */
    schema : Schema;

    rootItemId : number;

    /**
     * The URL for the route to load the data for exports
     */
    exportLoadDataUrl : string,

    /**
     * The key of the overview.
     */
    viewKey : string;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;

const setParam = origSetParam as <K extends keyof ( AllProps & DataTableAllProps )>( panelKey : string, paramKey : K, paramValue : ( AllProps & DataTableAllProps )[K] ) => SetParamAction;
const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps & DataTableAllProps> ) => SetParamsAction;


/**
 * Toolbar
 */
const Toolbar = ( props : AllProps ) => {
    const dispatch : AppDispatch       = useDispatch(),
          {
              columnDefinitions,
              columns,
              className,
              dataContext,
              dataStructure,
              exportLoadDataUrl,
              filters,
              rootItemId,
              schema,
              showClearSearchButton,
              showColumnOptionsButton,
              showColumnSortButton,
              showDownloadXlsxButton,
              showReloadButton,
              showViewSelector,
              sorters,
              tabIndexOffset,
              uid,
              viewKey,
              viewSelectorPanelProps
          }                            = props,
          user                         = useSelector( ( state : StoreState ) => state.session?.user ),
          messageCatalogTimestamp      = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          language                     = user
              ? user.language
              : undefined,
          allColumns                   = useMemo(
              () => {
                  return getAllColumns( columnDefinitions, columns, dataStructure, schema, false, false );
              },
              [
                  columnDefinitions,
                  columns,
                  dataStructure,
                  schema
              ]
          ),
          sortersWithField             = useMemo(
              () => {
                  return convertSortersToSortersWithField( sorters );
              },
              [
                  sorters
              ]
          ),
          onDownloadXlsxClick          = useCallback(
              () => {
                  const doFnc = async () => {
                      // request data
                      const fileContent = await XlsxExport.getXlsxFileForHistoryData(
                          exportLoadDataUrl,
                          uid,
                          schema,
                          rootItemId,
                          dataStructure,
                          columnDefinitions,
                          columns,
                          filters,
                          sorters
                      );

                      if ( fileContent === undefined ) {
                          dispatch( setMessage(
                              uid,
                              'toast',
                              'xlsxDownloadFailed',
                              {
                                  severity: 'error',
                                  summary : __( 'YN.base.common.xlsx.downloadFailed.title' ),
                                  detail  : parse( __( 'YN.base.common.xlsx.downloadFailed.text' ) )
                              }
                          ) );
                      }
                      else {
                          fileDownload( fileContent, 'FinderHistory_Properties', 'xlsx' );

                          dispatch( setMessage(
                              uid,
                              'toast',
                              'xlsxDownloadComplete',
                              {
                                  severity: 'success',
                                  summary : __( 'YN.base.common.xlsx.downloadComplete.title' ),
                                  detail  : parse( __( 'YN.base.common.xlsx.downloadComplete.text' ) )
                              }
                          ) );
                      }
                  };

                  // show download requested toast
                  dispatch( setMessage(
                      uid,
                      'toast',
                      'xlsxDownloadRequested',
                      {
                          severity: 'info',
                          summary : __( 'YN.base.common.xlsx.downloadRequested.title' ),
                          detail  : parse( __( 'YN.base.common.xlsx.downloadRequested.text' ) ),
                          closable: true,
                          sticky  : true
                      }
                  ) );

                  void doFnc();
              },
              [
                  columns,
                  columnDefinitions,
                  dataStructure,
                  exportLoadDataUrl,
                  filters,
                  language,
                  messageCatalogTimestamp,
                  rootItemId,
                  schema,
                  sorters,
                  uid
              ]
          ),
          onClearSearchButtonClick     = useCallback(
              () => {
                  dispatch( setParams(
                      uid,
                      {
                          doSearch: true,
                          filters : []
                      }
                  ) );
              },
              [
                  uid
              ]
          ),
          onReloadButtonClick          = useCallback(
              () => {
                  dispatch( setElusiveData( uid, { lastParams: undefined } ) );
                  dispatch( setParams(
                      uid,
                      {
                          doSearch: true,
                          columns : [],
                          filters : [],
                          sorters : []
                      }
                  ) );
              },
              [
                  uid
              ]
          ),
          onExpandColumnsToHeaderWidth = useCallback(
              () => {
                  dispatch( setParam( uid, 'autoLayout', true ) )
              },
              [
                  uid
              ]
          ),
          onExpandColumnsToScreenWidth = useCallback(
              () => {
                  dispatch( setParam( uid, 'forceFit', true ) )
              },
              [
                  uid
              ]
          ),
          onColumnVisibilityChange     = useCallback(
              ( allColumnsGrouped : ColumnConfigMenuColumn[] ) => {
                  const newColumns                              = applyAllColumnsToColumns( allColumnsGrouped as ColumnConfigMenuColumnsGroup[], columnDefinitions, columns ),
                        newSorters                              = ( [] as ColumnSorter[] ).concat( sorters ),
                        propsToSet : Partial<DataTableAllProps> = {
                            doSearch: true,
                            columns : newColumns
                        };

                  // remove sorters for removed columns
                  for ( const oldColumn of columns ) {

                      // column removed?
                      if ( !newColumns.find( column => column.field === oldColumn.field ) ) {
                          const sorterIndex = newSorters.findIndex( sorter => sorter.field === oldColumn.field );

                          // sorter found?
                          if ( sorterIndex !== -1 ) {
                              newSorters.splice( sorterIndex, 1 );

                              if ( !propsToSet.sorters ) {
                                  propsToSet.sorters = newSorters;
                              }
                          }
                      }
                  }

                  dispatch( setParams( uid, propsToSet ) );
              },
              [
                  uid,
                  sorters,
                  columns,
                  columnDefinitions
              ]
          ),
          onColumnSortChange           = useCallback(
              ( newSortersWithField : ColumnSortMenuColumnSorter[] ) => {
                  const propsToSet : Partial<DataTableAllProps> = {
                      doSearch: true,
                      sorters : convertSortersWithFieldToSorters( newSortersWithField, columnDefinitions )
                  };

                  dispatch( setParams( uid, propsToSet ) );
              },
              [
                  columnDefinitions,
                  uid
              ]
          );

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            if ( showDownloadXlsxButton ) {
                layoutComponents.left.push( {
                    type  : 'downloadXlsx',
                    params: {
                        key: `${uid}_downloadXlsxButton`,

                        className: 'yn-responsive-hidden',
                        onClick  : onDownloadXlsxClick,
                        tabIndex : tabIndexOffset + 50
                    }
                } );
            }

            layoutComponents.left.push( {
                type  : 'separator',
                params: {
                    className: 'yn-responsive-hidden'
                }
            } );

            if ( showClearSearchButton ) {
                layoutComponents.left.push( {
                    type  : 'clearSearchButton',
                    params: {
                        key: `${uid}_clearSearchButton`,

                        className: 'yn-responsive-hidden',
                        onClick  : onClearSearchButtonClick,
                        tabIndex : tabIndexOffset + 100
                    }
                } );
            }

            // right
            /*if ( showViewSelector ) {
                const viewOptions : ViewOption[] = [
                    {
                        key  : 'search',
                        type : 'BOOLEAN',
                        label: __( 'YN.base.frontend.dataTable.viewSelector.option.search.label' )
                    },
                    {
                        key  : 'forceFit',
                        type : 'BOOLEAN',
                        label: __( 'YN.base.frontend.dataTable.viewSelector.option.forceFit.label' )
                    }
                ];

                layoutComponents.right.push( {
                    type  : 'viewSelectorDropdown',
                    params: {
                        key: `${uid}_viewSelectorDropdown`,

                        panelKey: uid,

                        module                : `FinderHistory${user?.viewConfigSeparator}${viewKey}`,
                        views                 : this.getViewsForViewSelectorDropdown( viewKey ),
                        selectedView          : this.getSelectedViewForViewSelectorDropdown( viewKey, viewSelectorPanelProps ),
                        asConfiguredPanelProps: viewSelectorAsConfiguredPanelProps,
                        panelProps            : viewSelectorPanelProps,
                        viewOptions,
                        onSetView             : this.onViewSelectorSetView,
                        labeled               : true,
                        className             : 'yn-responsive-hidden',
                        style                 : { width: '18rem' }
                    }
                } );
            }*/

            if ( showColumnOptionsButton && user ) {
                layoutComponents.right.push( {
                    type  : 'columnConfigMenu',
                    params: {
                        key: `${uid}_columnConfig`,

                        uid,

                        noSort                      : true,
                        columns                     : allColumns as ColumnConfigMenuColumn[],
                        onChange                    : onColumnVisibilityChange,
                        onExpandColumnsToHeaderWidth: onExpandColumnsToHeaderWidth,
                        onExpandColumnsToScreenWidth: onExpandColumnsToScreenWidth,
                        className                   : 'yn-responsive-hidden',
                        tabIndex                    : tabIndexOffset + 130
                    }
                } );
            }

            if ( showColumnSortButton && user ) {
                layoutComponents.right.push( {
                    type  : 'columnSortMenu',
                    params: {
                        key: `${uid}_columnsort`,

                        uid,

                        columns  : allColumns,
                        sorters  : sortersWithField,
                        onChange : onColumnSortChange,
                        className: 'yn-responsive-hidden',
                        tabIndex : tabIndexOffset + 140
                    }
                } );
            }

            layoutComponents.right.push( {
                type  : 'separator',
                params: {
                    className: 'yn-responsive-hidden'
                }
            } );

            if ( showReloadButton ) {
                layoutComponents.right.push( {
                    type  : 'reloadButton',
                    params: {
                        key: `${uid}_reloadButton`,

                        confirm : false,
                        onClick : onReloadButtonClick,
                        tabIndex: tabIndexOffset + 150
                    }
                } );
            }

            const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                start={toolbarComponents.left}
                end={toolbarComponents.right}
                className={`${className} yn-panel-toolbar yn-overview-toolbar yn-toolbar-horizontal-separator-bottom`}
            />;
        },
        [
            allColumns,
            className,
            onClearSearchButtonClick,
            onColumnSortChange,
            onColumnVisibilityChange,
            onDownloadXlsxClick,
            onExpandColumnsToHeaderWidth,
            onExpandColumnsToScreenWidth,
            onReloadButtonClick,
            showClearSearchButton,
            showColumnOptionsButton,
            showColumnSortButton,
            showDownloadXlsxButton,
            showReloadButton,
            showViewSelector,
            sortersWithField,
            tabIndexOffset,
            uid,
            viewKey,
            viewSelectorPanelProps
        ]
    );
};
Toolbar.defaultProps = defaultProps;
Toolbar.displayName = 'FinderHistory-Toolbar';

export default Toolbar;
